import { Ellipsis } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Botao } from '@/componentes/Botao';
import { HistoriaNaMesa } from '@/historias/mesa';

import { alturaDoMenu, decidirNaRolagem, lugarDoMenu, type LugarDoMenu } from './mais-acoes';

/**
 * O MENU ANCORADO AO GATILHO (v2.1, p.30) — o "Mais ações" da linha.
 *
 * Colado ao gatilho, a 8 px; inverte se nao couber; de 200 a 360 px de
 * largura, raio 16 e elevacao 2 (p.16), e fecha no clique fora e no `Esc`.
 *
 * ## Por que ele existe
 *
 * O padrão que este componente substitui é o botão no topo agindo sobre o
 * "item escolhido": a pessoa clica na linha, sobe os olhos até o topo da tela,
 * e lá aperta o botão — que age sobre uma linha que ela já não está olhando.
 * Quando há mais de uma tabela na página, ou quando a lista é longa, o botão
 * do topo passa a agir sobre algo fora da tela.
 *
 * A regra do pedido: **até duas ações direto na linha, e o resto aqui**.
 * Duas cabem sem espremer a coluna; a terceira começa a competir com o dado.
 *
 * ## O que ele NÃO é
 *
 * Não é uma quarta sobreposição. A R01 nomeia três — modal ao centro, gaveta à
 * direita e **menu colado ao gatilho** —, e este é o terceiro. Ele nasce e
 * morre grudado no botão que o abriu, e some ao primeiro clique fora, ao `Esc`
 * e quando a rolagem leva o botão para fora da tela: menu que fica para trás
 * quando a linha sai da tela é um menu apontando para o nada. Enquanto o botão
 * está à vista, rolar faz o menu acompanhá-lo (`mais-acoes.ts`).
 */
export interface AcaoDoMenu {
  readonly rotulo: string;
  readonly aoEscolher: () => void;
  /** Quando existe, a ação aparece desligada e o texto diz por quê. */
  readonly motivoIndisponivel?: string;
  /** O código da história do item (modo histórias, p.47). */
  readonly historia?: string;
}

export function MaisAcoes({ acoes, rotulo = 'Mais ações', forma = 'texto' }: {
  readonly acoes: readonly AcaoDoMenu[];
  /** Nome acessível do gatilho; na forma `texto`, a tela também o mostra. */
  readonly rotulo?: string;
  /**
   * `icone`: os três pontos, sem texto — pedido do dono, 03/10/2026, e o
   * `ellipsis` do vocabulário da v2.1 (p.19). Botão redondo de 32 px, fixo no
   * canto direito da linha (a `Tabela` o recebe pela prop `menu`); o nome
   * continua lá para o leitor de tela e na dica.
   */
  readonly forma?: 'texto' | 'icone';
}) {
  const [aberto, setAberto] = useState(false);
  /* Onde o menu nasce: colado ao gatilho, alinhado pela direita — embaixo, ou
     em cima quando embaixo não cabe (R01, p.29: "invertendo o lado se não
     couber"). */
  const [lugar, setLugar] = useState<LugarDoMenu>({ direita: 0, topo: 0 });
  const caixa = useRef<HTMLDivElement>(null);
  const folha = useRef<HTMLDivElement>(null);
  const idDoMenu = useId();

  useEffect(() => {
    if (!aberto) return undefined;

    function foraDaqui(evento: MouseEvent) {
      const alvo = evento.target as Node;
      /* O menu mora fora da caixa (portal), então "fora" é fora dos dois. */
      if (caixa.current?.contains(alvo) || folha.current?.contains(alvo)) return;
      setAberto(false);
    }
    function pelaTecla(evento: KeyboardEvent) {
      if (evento.key === 'Escape') setAberto(false);
    }
    /* ROLAR NÃO FECHA POR SI (achado dos robôs, 20/10/2026): o próprio
       clique rola a página quando o botão estava meio escondido, e o menu
       abria e fechava no mesmo instante. Ele acompanha o gatilho e só fecha
       quando o gatilho sai da tela. */
    function aoRolar() {
      const r = caixa.current?.getBoundingClientRect();
      const janela = { largura: window.innerWidth, altura: window.innerHeight };
      if (!r || decidirNaRolagem(r, janela) === 'fechar') {
        setAberto(false);
        return;
      }
      setLugar(lugarDoMenu(r, alturaDoMenu(acoes), janela));
    }

    document.addEventListener('mousedown', foraDaqui);
    document.addEventListener('keydown', pelaTecla);
    window.addEventListener('scroll', aoRolar, true);
    return () => {
      document.removeEventListener('mousedown', foraDaqui);
      document.removeEventListener('keydown', pelaTecla);
      window.removeEventListener('scroll', aoRolar, true);
    };
  }, [aberto, acoes]);

  if (acoes.length === 0) return null;

  return (
    <div ref={caixa} className="relative inline-block">
      <Botao
        nivel={forma === 'icone' ? 'fantasma' : 'secundario'}
        {...(forma === 'icone' ? { tamanho: 'p' as const, icone: true } : {})}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? idDoMenu : undefined}
        {...(forma === 'icone' ? { title: rotulo } : {})}
        onClick={() => {
          const r = caixa.current?.getBoundingClientRect();
          if (r) setLugar(lugarDoMenu(r, alturaDoMenu(acoes), { largura: window.innerWidth, altura: window.innerHeight }));
          setAberto((estava) => !estava);
        }}
      >
        {forma === 'icone' ? (
          <>
            <Ellipsis className="size-[var(--icone-controle)] shrink-0" aria-hidden="true" />
            <span className="sr-only">{rotulo}</span>
          </>
        ) : (
          rotulo
        )}
      </Botao>

      {/* O menu vai para o <body>, por portal, e fica por cima de tudo. Dentro
          da linha ele ficava POR BAIXO das ações da linha seguinte: cada célula
          de ações é uma camada própria (sticky), e a de baixo é desenhada
          depois — pedido do dono, 03/10/2026. Fixo na tela e colado ao gatilho;
          rolar o reposiciona, e fecha só quando o botão sai da tela. */}
      {!aberto
        ? null
        : createPortal(
            <div
              ref={folha}
              id={idDoMenu}
              role="menu"
              style={'topo' in lugar ? { top: lugar.topo, right: lugar.direita } : { bottom: lugar.base, right: lugar.direita }}
              className="anima-menu fixed z-[var(--z-dica)] min-w-[var(--popover-min)] max-w-[var(--popover-max)] rounded-card border border-borda-sutil bg-flutuante p-1 shadow-elevado"
            >
              {acoes.map((a) => (
                <ItemDoMenu
                  key={a.rotulo}
                  acao={a}
                  aoFechar={() => {
                    setAberto(false);
                  }}
                />
              ))}
            </div>,
            document.body,
          )}
    </div>
  );
}

function ItemDoMenu({ acao, aoFechar }: { readonly acao: AcaoDoMenu; readonly aoFechar: () => void }): ReactNode {
  const item = <ItemSemHistoria acao={acao} aoFechar={aoFechar} />;
  return acao.historia ? <HistoriaNaMesa codigo={acao.historia}>{item}</HistoriaNaMesa> : item;
}

function ItemSemHistoria({ acao, aoFechar }: { readonly acao: AcaoDoMenu; readonly aoFechar: () => void }): ReactNode {
  const indisponivel = acao.motivoIndisponivel !== undefined;

  return (
    <button
      type="button"
      role="menuitem"
      disabled={indisponivel}
      className="txt-corpo block w-full cursor-pointer rounded-controle px-3 py-2 text-left text-tinta hover:bg-rebaixada disabled:cursor-not-allowed disabled:text-tinta-apagada disabled:hover:bg-transparent"
      onClick={() => {
        aoFechar();
        acao.aoEscolher();
      }}
    >
      {acao.rotulo}
      {/* O motivo fica NO LUGAR do clique, e não num balão que só aparece com
          o mouse parado: quem usa teclado nunca veria o balão. */}
      {indisponivel ? <span className="txt-linha2 block text-tinta-secundaria">{acao.motivoIndisponivel}</span> : null}
    </button>
  );
}
